<aside class="movie-facts-panel" *ngIf="movieDetails">

  <!-- Header -->
  <div class="facts-header">
    <div class="facts-header-left">
      <div class="header-icon-box">
        <i class="fas fa-circle-info"></i>
      </div>
      <div>
        <h3 class="facts-title">Movie Facts</h3>
        <span class="facts-subtitle">Quick Film Overview</span>
      </div>
    </div>
  </div>

  <!-- Key Metrics Highlights Grid -->
  <div class="facts-metrics-grid">

    <!-- Rating Card -->
    <div class="metric-card rating-card" *ngIf="movieDetails?.vote_average">
      <div class="metric-top">
        <i class="fas fa-star rating-star"></i>
        <span class="metric-label">Rating</span>
      </div>
      <div class="metric-value">
        <span class="rating-num">{{ movieDetails?.vote_average | number:'1.1-1' }}</span>
        <span class="rating-denom">/10</span>
        <span class="vote-count" *ngIf="movieDetails?.vote_count">({{ movieDetails?.vote_count | number }} votes)</span>
      </div>
    </div>

    <!-- Runtime Card -->
    <div class="metric-card" *ngIf="movieDetails?.runtime">
      <div class="metric-top">
        <i class="fas fa-clock metric-icon"></i>
        <span class="metric-label">Runtime</span>
      </div>
      <span class="metric-value">{{ formatRuntime(movieDetails?.runtime) }}</span>
    </div>

    <!-- Budget Card -->
    <div class="metric-card" *ngIf="movieDetails?.budget && movieDetails?.budget > 0">
      <div class="metric-top">
        <i class="fas fa-wallet metric-icon"></i>
        <span class="metric-label">Budget</span>
      </div>
      <span class="metric-value">${{ formatCurrency(movieDetails?.budget) }}</span>
    </div>

    <!-- Revenue Card -->
    <div class="metric-card" *ngIf="movieDetails?.revenue && movieDetails?.revenue > 0">
      <div class="metric-top">
        <i class="fas fa-chart-line metric-icon"></i>
        <span class="metric-label">Revenue</span>
      </div>
      <span class="metric-value">${{ formatCurrency(movieDetails?.revenue) }}</span>
    </div>
  </div>

  <!-- Tagline Block (if exists) -->
  <div class="facts-tagline" *ngIf="movieDetails?.tagline">
    <i class="fas fa-quote-left quote-mark"></i>
    <p class="tagline-quote">{{ movieDetails?.tagline }}</p>
  </div>

  <!-- Structured Details Rows -->
  <div class="facts-rows-list">
    <!-- Original Title -->
    <div class="fact-row" *ngIf="movieDetails?.original_title && movieDetails?.original_title !== movieDetails?.title">
      <div class="fact-row-header">
        <i class="fas fa-film"></i>
        <span>Original Title</span>
      </div>
      <span class="fact-row-content">{{ movieDetails?.original_title }}</span>
    </div>

    <!-- Release Date -->
    <div class="fact-row" *ngIf="movieDetails?.release_date">
      <div class="fact-row-header">
        <i class="fas fa-calendar-alt"></i>
        <span>Release Date</span>
      </div>
      <span class="fact-row-content">{{ movieDetails?.release_date | date:'MMMM d, yyyy' }}</span>
    </div>

    <!-- Duration -->
    <div class="fact-row" *ngIf="movieDetails?.runtime">
      <div class="fact-row-header">
        <i class="fas fa-clock"></i>
        <span>Duration</span>
      </div>
      <span class="fact-row-content">{{ movieDetails?.runtime }} minutes ({{ formatRuntime(movieDetails?.runtime)
        }})</span>
    </div>

    <!-- Original Language -->
    <div class="fact-row" *ngIf="movieDetails?.original_language">
      <div class="fact-row-header">
        <i class="fas fa-language"></i>
        <span>Original Language</span>
      </div>
      <span class="fact-row-content language-badge">
        {{ getLanguageName(movieDetails?.original_language) }}
        <span class="code-pill">({{ movieDetails?.original_language | uppercase }})</span>
      </span>
    </div>

    <!-- Budget -->
    <div class="fact-row" *ngIf="movieDetails?.budget !== undefined">
      <div class="fact-row-header">
        <i class="fas fa-dollar-sign"></i>
        <span>Budget</span>
      </div>
      <span class="fact-row-content">
        {{ movieDetails?.budget > 0 ? ('$' + (movieDetails?.budget | number)) : 'Not Disclosed' }}
      </span>
    </div>

    <!-- Revenue -->
    <div class="fact-row" *ngIf="movieDetails?.revenue !== undefined">
      <div class="fact-row-header">
        <i class="fas fa-money-bill-wave"></i>
        <span>Revenue</span>
      </div>
      <span class="fact-row-content">
        {{ movieDetails?.revenue > 0 ? ('$' + (movieDetails?.revenue | number)) : 'Not Disclosed' }}
      </span>
    </div>

    <!-- Box Office Profit / Net -->
    <div class="fact-row" *ngIf="getProfit(movieDetails?.revenue, movieDetails?.budget) as profit">
      <div class="fact-row-header">
        <i class="fas fa-arrow-trend-up"></i>
        <span>Box Office Net</span>
      </div>
      <span class="fact-row-content profit-badge" [class.profit-positive]="profit.isProfit"
        [class.profit-negative]="!profit.isProfit">
        <i [class]="profit.isProfit ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
        {{ profit.text }}
      </span>
    </div>

    <!-- Production Countries -->
    <div class="fact-row" *ngIf="movieDetails?.production_countries?.length">
      <div class="fact-row-header">
        <i class="fas fa-earth-americas"></i>
        <span>Origin Country</span>
      </div>
      <div class="fact-row-content">
        <span *ngFor="let country of movieDetails?.production_countries; let last = last">
          {{ country?.name }}<span *ngIf="!last">, </span>
        </span>
      </div>
    </div>

    <!-- Genres -->
    <div class="fact-block" *ngIf="movieDetails?.genres?.length">
      <div class="fact-row-header">
        <i class="fas fa-tags"></i>
        <span>Genres</span>
      </div>
      <div class="pills-container">
        <span class="info-pill genre-pill" *ngFor="let genre of movieDetails?.genres">
          {{ genre.name }}
        </span>
      </div>
    </div>

    <!-- Production Companies -->
    <div class="fact-block" *ngIf="movieDetails?.production_companies?.length">
      <div class="fact-row-header">
        <i class="fas fa-building"></i>
        <span>Production</span>
      </div>
      <div class="companies-container">
        <div class="company-badge-box" *ngFor="let company of movieDetails?.production_companies">
          <img *ngIf="company.logo_path" [src]="'https://image.tmdb.org/t/p/w200' + company.logo_path"
            [alt]="company.name" class="company-logo" (error)="onCompanyLogoError($event)">
          <span class="company-name">{{ company.name }}</span>
        </div>
      </div>
    </div>

    <!-- Official Website Action Button -->
    <div class="website-action" *ngIf="movieDetails?.homepage">
      <a [href]="movieDetails?.homepage" target="_blank" rel="noopener noreferrer" class="website-link-btn">
        <i class="fas fa-arrow-up-right-from-square"></i>
        <span>Visit Official Website</span>
      </a>
    </div>
  </div>
</aside>